import { UseQueryOptions, useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { RemotePassReviewRepo } from './PassReviewRepo';
import PassReviewPostListGetRq from '@domain/rq/passReview/PassReviewPostListGetRq';
import PassReviewPostSaveRq from '@domain/rq/passReview/PassReviewPostSaveRq';
import PassReviewQuestionListRq from '@domain/rq/passReview/PassReviewQuestionListRq';
import PassReviewPostListGetVO from '@domain/vo/passReview/PassReviewPostListGetVO';

const keys = ['passReview'];
const repo = new RemotePassReviewRepo();

//합격 후기 게시글 목록 조회
const fetchPassReviewPosts = async (rq: PassReviewPostListGetRq) => {
  const { data } = await repo.fetchPassReviewPosts(rq);
  return new PassReviewPostListGetVO(data);
};

export const usePassReviewPosts = (rq: PassReviewPostListGetRq) => {
  return useInfiniteQuery([...keys, 'list', rq], () => fetchPassReviewPosts(rq), {
    getNextPageParam: ({ pages: { page, totalPages } }) => (page < totalPages ? page + 1 : undefined),
    keepPreviousData: true,
  });
};

//합격 후기 게시글 등록
const createPassReviewPosts = async (rq: PassReviewPostSaveRq) => {
  const { data } = await repo.createPassReviewPosts(rq);
  return data;
};

export const useCreatePassReviewPosts = () => {
  return useMutation(createPassReviewPosts);
};

//합격 후기 게시글 상세 조회
const fetchPassReviewPostDetail = async (sn: number) => {
  const { data } = await repo.fetchPassReviewPostDetail(sn);
  return data;
};

export const usePassReviewPostDetail = (sn: number) => {
  return useQuery([...keys, 'detail', sn], () => fetchPassReviewPostDetail(sn), {
    enabled: !!sn,
  });
};

//합격 후기 게시글 수정
const updatePassReviewPosts = async ({ sn, rq }: { sn: number; rq: PassReviewPostSaveRq }) => {
  const { data } = await repo.updatePassReviewPosts(sn, rq);
  return data;
};

export const useUpdatePassReviewPosts = () => {
  return useMutation(updatePassReviewPosts);
};

//합격 후기 게시글 삭제
const deletePassReviewPosts = async (sn: number) => {
  const { data } = await repo.deletePassReviewPosts(sn);
  return data;
};

export const useDeletePassReviewPosts = () => {
  return useMutation(deletePassReviewPosts);
};

//합격 후기 게시글 질문 목록 조회
const fetchPassReviewPostQuestions = async (rq: PassReviewQuestionListRq) => {
  const { data } = await repo.fetchPassReviewPostQuestions(rq);
  return data;
};

export const usePassReviewPostQuestions = (rq: PassReviewQuestionListRq) => {
  return useQuery([...keys, 'questions', rq], () => fetchPassReviewPostQuestions(rq), {
    enabled: !!rq.type,
  });
};
//합격 후기 배너 목록 조회
const fetchPassReviewBanners = async () => {
  const { data } = await repo.fetchPassReviewBanners();
  return data;
};

export const usePassReviewBanners = () => {
  return useQuery([...keys, 'banners'], fetchPassReviewBanners);
};

//합격 후기 댓글 목록 조회
const fetchPassReviewPostComments = async (sn: number) => {
  const { data } = await repo.fetchPassReviewPostComments(sn);
  return data;
};

export const usePassReviewPostComments = (sn: number, options?: UseQueryOptions) => {
  return useQuery([...keys, 'comments', sn], () => fetchPassReviewPostComments(sn), {
    ...(options as any),
  });
};

//합격 후기 댓글 등록
const createPassReviewPostComments = async ({ sn, comment }: { sn: number; comment: string }) => {
  const { data } = await repo.createPassReviewPostComments(sn, comment);
  return data;
};

export const useCreatePassReviewPostComments = () => {
  return useMutation(createPassReviewPostComments);
};

//합격 후기 댓글 수정
const updatePassReviewPostComments = async ({
  sn,
  commentSn,
  comment,
}: {
  sn: number;
  commentSn: number;
  comment: string;
}) => {
  const { data } = await repo.updatePassReviewPostComments(sn, commentSn, comment);
  return data;
};

export const useUpdatePassReviewPostComments = () => {
  return useMutation(updatePassReviewPostComments);
};

//합격 후기 댓글 삭제
const deletePassReviewPostComments = async ({ sn, commentSn }: { sn: number; commentSn: number }) => {
  const { data } = await repo.deletePassReviewPostComments(sn, commentSn);
  return data;
};

export const useDeletePassReviewPostComments = () => {
  return useMutation(deletePassReviewPostComments);
};

//합격 후기 게시글 Top5
const fetchPassReviewPostsTop = async () => {
  const { data } = await repo.fetchPassReviewPostsTop();
  return data;
};

export const usePassReviewPostsTop = () => {
  return useQuery([...keys, 'top'], fetchPassReviewPostsTop);
};
